import { useState, useEffect } from 'react';
import { useIntl } from 'umi';
import { DEFAULT_VALUE_DAY, DEFAULT_VALUE_MONTH } from '../_config/constant';

import type { IDateType, IDateValue } from '@/pages/intelligence/types/common';
import type { IPickerValue } from '../types';

export default function useSelector(props: {
  defaultValue: IPickerValue;
  onConfirm?: (type: IDateType, value: IDateValue) => void;
  onCancel?: () => void;
  onClose?: () => void;
}) {
  const { defaultValue, onConfirm, onCancel, onClose } = props;

  const { formatMessage } = useIntl();

  const [type, setType] = useState<IDateType>(defaultValue?.type ?? DEFAULT_VALUE_DAY?.type);
  const [rangeDate, setRangeDate] = useState<IDateValue>(defaultValue?.value ?? DEFAULT_VALUE_DAY?.value);
  const [errorMessage, setErrorMessage] = useState(['', '']);
  const getErrorMessage = (type: IDateType, rangeDate: IDateValue) => {
    // message 优先级，索引越大优先级越低
    const errorMessageList = [
      formatMessage({ id: 'dbi_range_month_picker_please_enter_correct_date' }),
      formatMessage({ id: 'dbi_range_month_picker_over_max_date' }),
      formatMessage({ id: 'dbi_range_month_picker_over_365_dates' }),
    ];

    const isInValid = (value: any): boolean =>
      // 不能非数字、小于0、小数
      isNaN(value) || Number(value) <= 0 || Number.parseInt(value, 10) !== Number.parseFloat(value);
    const outOfRange = (value: number): boolean => {
      const max = type === 'date' ? 1095 : 36;
      return value > max;
    };

    const outOfMaxLength = (type: IDateType, rangeDate: IDateValue): boolean =>
      // @ts-ignore
      Math.abs(rangeDate[0] - rangeDate[1]) > (type === 'date' ? 364 : 11);
    let errorMessgeStart: string = (() => {
      if ((Number(rangeDate[0]) > Number(rangeDate[1]) && rangeDate[1]) || isInValid(rangeDate[0])) {
        return errorMessageList[0];
      }
      // @ts-ignore
      if (outOfRange(rangeDate[0])) {
        return errorMessageList[1];
      }
      return '';
    })();
    let errorMessageEnd = (() => {
      if (isInValid(rangeDate[1])) {
        return errorMessageList[0];
      }
      // @ts-ignore
      if (outOfRange(rangeDate[1])) {
        return errorMessageList[1];
      }
      if (Number(rangeDate[0]) < Number(rangeDate[1]) && outOfMaxLength(type, rangeDate)) {
        return errorMessageList[2];
      }
      return '';
    })();

    if (errorMessageEnd && errorMessgeStart) {
      const startIndex = errorMessageList.findIndex((message) => message === errorMessgeStart);
      const endIndex = errorMessageList.findIndex((message) => message === errorMessageEnd);
      if (startIndex >= endIndex) {
        errorMessgeStart = '';
      } else {
        errorMessageEnd = '';
      }
    }

    return [errorMessgeStart, errorMessageEnd];
  };

  const handleTypeChange = (type: IDateType) => {
    const errorMessage = getErrorMessage(type, rangeDate);
    setErrorMessage(errorMessage);
    setType(type);
    setRangeDate(type === 'date' ? DEFAULT_VALUE_DAY?.value : DEFAULT_VALUE_MONTH?.value);
  };

  const handleRangeDateChange = (newRangeDate: IDateValue) => {
    const errorMessage = getErrorMessage(type, newRangeDate);
    setErrorMessage(errorMessage);
    setRangeDate(newRangeDate);
  };

  const handleConfirm = () => {
    if (errorMessage?.some((item) => item)) return;
    onConfirm?.(type, rangeDate);
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  useEffect(() => {
    setErrorMessage(getErrorMessage(type, rangeDate));
  }, [rangeDate, type]);

  return {
    type,
    rangeDate,
    errorMessage,
    handleConfirm,
    handleCancel,
    handleTypeChange,
    handleRangeDateChange,
  };
}
